import React, { useState, useEffect, useCallback } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { loginRequest } from '../authConfig';

const toLocal = (utcDateString) => {
    if (!utcDateString) return '';
    const date = utcDateString.endsWith('Z') ? new Date(utcDateString) : new Date(utcDateString + 'Z');
    return date.toLocaleString();
};

function MyHistory({ onNavigate }) {
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || {});
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = 20;

    const getToken = useCallback(async () => {
        try {
            const result = await instance.acquireTokenSilent({ ...loginRequest, account });
            return result.accessToken;
        } catch {
            const result = await instance.acquireTokenRedirect(loginRequest);
            return result?.accessToken;
        }
    }, [instance, account]);

    const fetchHistory = useCallback(async () => {
        try {
            setLoading(true);
            const token = await getToken();
            const response = await fetch(`/api/history/assist?page=${page}&pageSize=${pageSize}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch history');
            const data = await response.json();
            setSessions(data.data);
            setTotalCount(data.totalCount);
            setTotalPages(data.totalPages);
        } catch (err) {
            console.error('Error fetching history:', err);
        } finally {
            setLoading(false);
        }
    }, [getToken, page]);

    useEffect(() => { fetchHistory(); }, [fetchHistory]);

    const handleResumeSession = (sessionId) => {
        onNavigate('assistant', { resumeSessionId: sessionId });
    };

    return (
        <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
            <div className="page-header" style={{ marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0 }}>Assistant History</h2>
                <p style={{ margin: '0.25rem 0 0', color: '#6c757d', fontSize: '0.9rem' }}>
                    Your recent AI assistant conversations — {totalCount} total
                </p>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>Loading...</div>
            ) : sessions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>💬</div>
                    <div style={{ fontWeight: 600, color: '#1a4d2e', marginBottom: '0.5rem' }}>No conversations yet</div>
                    <div style={{ color: '#6c757d', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                        Start a conversation with the AI Assistant to see your history here.
                    </div>
                    <button
                        onClick={() => onNavigate('assistant')}
                        style={{ background: '#4a9e6d', color: 'white', border: 'none', padding: '0.5rem 1.25rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500 }}
                    >
                        Start a Conversation
                    </button>
                </div>
            ) : (
                <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {sessions.map(session => (
                            <div
                                key={session.sessionId}
                                style={{ background: 'white', borderRadius: '10px', border: '1px solid #e0e4e8', padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', gap: '1rem', cursor: 'pointer', transition: 'border-color 0.15s' }}
                                onClick={() => handleResumeSession(session.sessionId)}
                                onMouseEnter={e => e.currentTarget.style.borderColor = '#4a9e6d'}
                                onMouseLeave={e => e.currentTarget.style.borderColor = '#e0e4e8'}
                            >
                                <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>💬</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 500, color: '#1a4d2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '0.2rem' }}>
                                        {session.firstMessage}
                                    </div>
                                    <div style={{ fontSize: '0.78rem', color: '#adb5bd', display: 'flex', gap: '0.75rem' }}>
                                        <span>{toLocal(session.lastActivity)}</span>
                                        <span>·</span>
                                        <span>{session.messageCount} message{session.messageCount !== 1 ? 's' : ''}</span>
                                        {session.hasAttachments && <><span>·</span><span>📎</span></>}
                                    </div>
                                </div>
                                <div style={{ color: '#4a9e6d', fontSize: '0.8rem', fontWeight: 500, flexShrink: 0 }}>
                                    Resume →
                                </div>
                            </div>
                        ))}
                    </div>

                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
                            <button onClick={() => setPage(1)} disabled={page === 1} className="btn btn-sm">First</button>
                            <button onClick={() => setPage(p => p - 1)} disabled={page === 1} className="btn btn-sm">Prev</button>
                            <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>{page} / {totalPages}</span>
                            <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages} className="btn btn-sm">Next</button>
                            <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="btn btn-sm">Last</button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default MyHistory;